/** Distinguir um clique direito de um arrasto com o botão direito.
 *
 *  O botão direito passou a rodar a câmara, a pedido do Rui, para não
 *  ser preciso Shift + roda para rodar. Mas o direito já fazia duas
 *  coisas: abrir o menu de contexto, e nas medições acabar a área ou
 *  cancelar o comprimento.
 *
 *  No Windows o browser dispara o `contextmenu` quando se LARGA o botão,
 *  e dispara-o também depois de um arrasto. Sem isto, cada rotação
 *  acabava com o menu aberto, e rodar a meio de uma medição acabava com
 *  ela. Por isso quem trata do `contextmenu` pergunta primeiro se o
 *  último uso do direito foi um arrasto, e se foi, não faz nada.
 *
 *  Uma limitação que fica escrita: no macOS o `contextmenu` dispara ao
 *  CARREGAR, antes de haver arrasto nenhum, e aí o menu abre na mesma e
 *  a rotação faz-se com ele aberto. A equipa trabalha em Windows. Se um
 *  dia isso mudar, o caminho é abrir o menu no `pointerup` do direito
 *  sem arrasto, em vez de no `contextmenu`.
 */

/** A partir de quantos píxeis um clique passa a ser um arrasto. O
 *  mesmo limiar que o clique esquerdo usa para seleccionar. */
export const LIMIAR_PX = 4

/** Andou o suficiente para ser um arrasto? */
export function foiArrasto(dx: number, dy: number, limiar = LIMIAR_PX): boolean {
  return Math.hypot(dx, dy) > limiar
}

let inicio: { x: number; y: number } | null = null
let maiorDistancia = 0

function aoCarregar(e: PointerEvent) {
  if (e.button !== 2) return
  inicio = { x: e.clientX, y: e.clientY }
  maiorDistancia = 0
}

function aoMexer(e: PointerEvent) {
  // `buttons` é uma máscara: o 2 é o direito, e continua lá enquanto
  // ele estiver carregado, mesmo com outros botões a mexer.
  if (!inicio || (e.buttons & 2) === 0) return
  const d = Math.hypot(e.clientX - inicio.x, e.clientY - inicio.y)
  if (d > maiorDistancia) maiorDistancia = d
}

/** O último uso do botão direito foi um arrasto? */
export function ultimoDireitoFoiArrasto(): boolean {
  return foiArrasto(maiorDistancia, 0)
}

/**
 * Começa a vigiar o botão direito. Devolve a função que pára.
 *
 * Em captura, para ver o `pointerdown` antes de o camera-controls o
 * apanhar e antes de qualquer outro tratar do clique.
 */
export function vigiarBotaoDireito(alvo: EventTarget = window): () => void {
  alvo.addEventListener('pointerdown', aoCarregar as EventListener, {
    capture: true,
  })
  alvo.addEventListener('pointermove', aoMexer as EventListener, {
    capture: true,
  })
  return () => {
    alvo.removeEventListener('pointerdown', aoCarregar as EventListener, {
      capture: true,
    })
    alvo.removeEventListener('pointermove', aoMexer as EventListener, {
      capture: true,
    })
    inicio = null
    maiorDistancia = 0
  }
}
